import {FirebaseApp, getApp, initializeApp} from 'firebase/app';
import {Auth as FirebaseAuth, getAuth} from 'firebase/auth';
import {Database as FirebaseDatabase, getDatabase} from 'firebase/database';
import {FirebaseStorage, getStorage} from 'firebase/storage';
import React from 'react';
import 'firebase/storage';

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyD4-aU00QSfSlvcppAZoe9issOBSb3R6MQ',
  authDomain: 'monitron-dev.firebaseapp.com',
  databaseURL: 'https://monitron-dev.firebaseio.com',
  projectId: 'monitron-dev',
  storageBucket: 'monitron-dev.appspot.com',
  messagingSenderId: '579167012312',
  appId: '1:579167012312:web:89d72e5e3b214962039ead',
  measurementId: 'G-40LLCV79FJ',
};

export function initializeFirebase() {
  initializeApp(FIREBASE_CONFIG);
}

// Holds various Firebase types that consumers may want to interact with.
type FirebaseObjects = {
  app: FirebaseApp;
  auth: FirebaseAuth;
  database: FirebaseDatabase;
  storage: FirebaseStorage;
};

export const FirebaseContext = React.createContext<FirebaseObjects | undefined>(undefined);

// Allows access to the Firebase app and some of its sub-objects.
//
// Will initialize the app if it hasn’t been, but does not try to delete it on
// unmount.
const FirebaseProvider: React.FunctionComponent<React.PropsWithChildren<{config?: object}>> = ({
  config = FIREBASE_CONFIG,
  children,
}) => {
  let app: FirebaseApp;

  try {
    app = getApp();
  } catch {
    app = initializeApp(config);
  }

  // We definitely need these objects to stay consistent while the app is
  // being used, since they’re dependencies for any useEffect hooks that
  // interact with Firebase.
  const firebaseObjects: FirebaseObjects = React.useMemo(
    () => ({
      app: app,
      auth: getAuth(app),
      database: getDatabase(app),
      storage: getStorage(app),
    }),
    [app]
  );

  return <FirebaseContext.Provider value={firebaseObjects}>{children}</FirebaseContext.Provider>;
};

const useFirebase = () => {
  const firebaseObjects = React.useContext(FirebaseContext);

  if (!firebaseObjects) {
    throw new Error('This hook must be called beneath FirebaseProvider');
  }

  return firebaseObjects;
};

export const useFirebaseAuth = () => {
  const {auth} = useFirebase();
  return auth;
};

export const useFirebaseDatabase = () => {
  const {database} = useFirebase();
  return database;
};

export const useFirebaseStorage = () => {
  const {storage} = useFirebase();
  return storage;
};

export default FirebaseProvider;
